import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";

const LINE_EASE = [0.16, 1, 0.3, 1];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.25 + i * 0.16, duration: 1.0, ease: LINE_EASE },
  }),
};

export default function Hero() {
  const { scrollYProgress } = useScroll();
  const glowY = useTransform(scrollYProgress, [0, 0.35], [0, 140]);
  const fade = useTransform(scrollYProgress, [0, 0.3], [1, 0.2]);

  return (
    <section id="top" className="relative flex min-h-screen flex-col justify-end overflow-hidden">
      <motion.div
        aria-hidden="true"
        style={{ y: glowY, opacity: fade }}
        className="pointer-events-none absolute -top-40 right-[-15%] h-[34rem] w-[34rem] rounded-full bg-champagne/10 blur-[140px]"
      />
      <motion.div
        aria-hidden="true"
        style={{ y: glowY }}
        className="pointer-events-none absolute bottom-[-20%] left-[-10%] h-[28rem] w-[28rem] rounded-full bg-[#16303a]/60 blur-[120px]"
      />

      <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-40 sm:px-8 sm:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          data-testid="hero-eyebrow"
          className="mb-8 font-mono text-[11px] uppercase tracking-[0.35em] text-champagne"
        >
          Pebble Beach · Monterey · Carmel · Big Sur · SJC · SFO · OAK · Napa Valley · Lake Tahoe
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-serif text-5xl font-light leading-[1.04] tracking-tight sm:text-7xl lg:text-8xl"
        >
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={lineVariants} initial="hidden" animate="visible" className="block">
              The Fleet,
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span custom={1} variants={lineVariants} initial="hidden" animate="visible" className="block italic text-champagne">
              composed for the coast.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8, ease: LINE_EASE }}
          data-testid="hero-subtext"
          className="mt-8 max-w-xl text-base leading-relaxed text-smoke"
        >
          Four vehicles, kept to a single standard: immaculate. Airport transfers, 17-Mile Drive
          tours, and group logistics across the Monterey Peninsula — chauffeured with quiet precision.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8, ease: LINE_EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#fleet"
            data-testid="hero-explore-button"
            className="group flex items-center gap-3 bg-champagne px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-onyx transition-colors duration-300 hover:bg-champagne-light"
          >
            Explore the Fleet
            <ArrowDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-y-0.5" />
          </a>
          <a
            href="https://wa.me/14158885550?text=Hi%20Pebble%20Beach%20Luxury%20Ride%2C%20I%27d%20like%20to%20request%20a%20quote."
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-button"
            className="flex items-center gap-2 border border-line px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:border-champagne/50 hover:text-champagne"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            Concierge WhatsApp
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-16 grid grid-cols-3 gap-6 border-t border-line pt-8 sm:mt-20"
          data-testid="hero-stats"
        >
          {[
            ["04", "Vehicles"],
            ["1–13", "Passengers"],
            ["24/7", "Concierge"],
          ].map(([num, label]) => (
            <div key={label}>
              <p className="font-serif text-3xl text-bone sm:text-4xl">{num}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">{label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
